import { useCallback, useMemo, useState } from 'react';
import {
	getApiErrorMessage,
	useCreateStateReference,
	useDeleteStateReference,
	useGetStateReferences,
	useUpdateStateReference,
} from '@practium/features/api';
import { useToast, type ConfirmOptions } from '@practium/ui';
import type {
	ReferenceFormValue,
	ReferenceKey,
	ReferenceUpdate,
	StateSectionKey,
} from '../types/parameters.types';
import { buildReferencePayload, normalizeReference } from '../utils/referenceForm';
import { validateStateReference } from '../utils/validation';

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

export const useStateReferencesPage = (confirm: Confirm) => {
	const toast = useToast();
	const { data: references = [], isLoading, isError } = useGetStateReferences();
	const createReference = useCreateStateReference();
	const updateReference = useUpdateStateReference();
	const deleteReference = useDeleteStateReference();

	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [section, setSection] = useState<StateSectionKey>('macro');

	const selectedRaw = references.find((item) => item._id === selectedId) ?? references[0];
	const selected = useMemo(() => (selectedRaw ? normalizeReference(selectedRaw) : null), [selectedRaw]);

	const saveSection = useCallback(
		async (update: ReferenceUpdate, message: string) => {
			if (!selected) return false;
			const validationError = validateStateReference(
				{ ...selected, ...update },
				Object.keys(update) as ReferenceKey[]
			);
			if (validationError) {
				toast.error(validationError);
				return false;
			}
			try {
				await updateReference.mutateAsync({ id: selected._id, data: update });
				toast.success(message);
				return true;
			} catch (error) {
				toast.error(getApiErrorMessage(error, 'Erro ao salvar. Tente novamente.'));
				return false;
			}
		},
		[selected, toast, updateReference]
	);

	const confirmDelete = useCallback(
		(label: string) =>
			confirm({
				title: 'Excluir item',
				message: `Deseja excluir ${label}? Esta ação não pode ser desfeita.`,
				confirmText: 'Excluir',
				cancelText: 'Cancelar',
				variant: 'danger',
			}),
		[confirm]
	);

	const createRegion = useCallback(
		async (form: ReferenceFormValue) => {
			const base = references.find((item) => item._id === form.baseReferenceId);
			try {
				const created = await createReference.mutateAsync(buildReferencePayload(form, base));
				setSelectedId(created._id);
				toast.success('Região criada com sucesso!');
				return true;
			} catch (error) {
				toast.error(getApiErrorMessage(error, 'Erro ao criar região.'));
				return false;
			}
		},
		[createReference, references, toast]
	);

	const updateRegion = useCallback(
		async (form: ReferenceFormValue) => {
			if (!selected) return false;
			try {
				await updateReference.mutateAsync({
					id: selected._id,
					data: {
						stateCode: form.stateCodes,
						referenceCode: form.name.trim(),
					},
				});
				toast.success('Região atualizada com sucesso!');
				return true;
			} catch (error) {
				toast.error(getApiErrorMessage(error, 'Erro ao atualizar região.'));
				return false;
			}
		},
		[selected, toast, updateReference]
	);

	const deleteRegion = useCallback(async () => {
		if (!selected) return false;
		const confirmed = await confirm({
			title: 'Excluir região',
			message: `Deseja excluir a região "${selected.referenceCode}"? Os estados dela ficarão sem parâmetros.`,
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (!confirmed) return false;
		try {
			await deleteReference.mutateAsync(selected._id);
			setSelectedId(null);
			toast.success('Região excluída com sucesso!');
			return true;
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Erro ao excluir região.'));
			return false;
		}
	}, [confirm, deleteReference, selected, toast]);

	return {
		references,
		selected,
		section,
		isLoading,
		isError,
		isSaving: updateReference.isPending,
		setSection,
		selectReference: setSelectedId,
		saveSection,
		confirmDelete,
		createRegion,
		updateRegion,
		deleteRegion,
	};
};
